import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Search, UserPlus } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { useEmployees } from '../lib/queries';
import { EmptyState, PageHeader, Spinner, cn } from '../components/ui';
import { EmployeeCreateDialog } from '../components/EmployeeCreateDialog';

/** Mitarbeiter als Geräteträger — Systemkonten liegen unter Verwaltung. */
export function EmployeesPage() {
  const auth = useAuth();
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search.trim()), 250);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading } = useEmployees({
    q: debounced || undefined,
    is_active: showInactive ? undefined : true,
  });

  return (
    <div>
      <PageHeader
        eyebrow="Personen"
        title="Mitarbeiter"
        subtitle={data ? `${data.rows.length} Personen` : undefined}
        actions={
          auth.can('employee.write') && (
            <button type="button" onClick={() => setCreateOpen(true)} className="btn btn-accent btn-md">
              <UserPlus className="h-4 w-4" aria-hidden />
              Anlegen
            </button>
          )
        }
      />

      <div className="mb-5 flex flex-wrap items-center gap-3">
        <div className="relative min-w-56 flex-1">
          <Search
            className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-[var(--color-fg3)]"
            aria-hidden
          />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Name oder E-Mail"
            className="field pl-9"
            aria-label="Mitarbeiter suchen"
          />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(event) => setShowInactive(event.target.checked)}
            className="h-4 w-4 accent-[var(--color-accent)]"
          />
          Ausgeschiedene mitzeigen
        </label>
      </div>

      {isLoading && <Spinner label="Personen werden geladen …" />}

      {data && data.rows.length === 0 && (
        <EmptyState title="Niemand gefunden" description="Andere Schreibweise probieren." />
      )}

      {data && data.rows.length > 0 && (
        <ul className="stagger grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {data.rows.map((employee) => (
            <li key={employee.id}>
              <Link
                to="/personen/$employeeId"
                params={{ employeeId: String(employee.id) }}
                className="surface card-link flex items-center gap-3 px-5 py-4 no-underline"
              >
                <span
                  aria-hidden
                  className={cn(
                    'grid h-9 w-9 shrink-0 place-items-center rounded-full text-sm font-semibold',
                    employee.is_active
                      ? 'bg-[var(--color-khadi-forest-100)] text-[var(--color-khadi-forest-700)]'
                      : 'bg-[var(--color-khadi-cream-200)] text-[var(--color-fg3)]',
                  )}
                >
                  {initials(employee.name)}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[0.9375rem] font-semibold text-[var(--color-fg)]">
                    {employee.name}
                  </span>
                  <span className="block truncate text-sm text-[var(--color-fg3)]">
                    {employee.is_active ? employee.email ?? '—' : 'ausgeschieden'}
                  </span>
                </span>
                <span className="tabular shrink-0 text-sm text-[var(--color-fg2)]">
                  {employee.assets_count > 0 ? employee.assets_count : ''}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {createOpen && <EmployeeCreateDialog onClose={() => setCreateOpen(false)} />}
    </div>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}
